import { gql } from 'graphql-tag'

import type { ReportRecordFields } from '@tribeplatform/gql-client/graphql'

import {
  ActionFields,
  EventRegistrationFields,
  EventRegistrationGQLFields,
  ActionGQLFields,
  SpaceGQLFields,
  FileGQLFields,
  MediaGQLFields,
  EmbedGQLFields,
  MemberGQLFields,
  EventNotificationSettingsGQLFields,
  CustomSeoDetailGQLFields,
  TagGQLFields,
  ActionPermissionsGQLFields,
  AutocompletePhysicalLocationResultGQLFields,
  AutocompletePhysicalLocationResultFields,
  PlaceDetailsGQLFields,
  PlaceDetailsFields,
  EventRecurrenceFields,
  EventRecurrenceGQLFields,
  CalendarInviteDataGQLFields,
} from '../../types/fields.generated.js'
import { reportRecordGQLFields } from '../report/reportRecord.fields.js'

const EVENT_LIST_FIELDS = `
  id
  title
  shortContent
  startTime
  endTime
  timeZone
  status
  url
  relativeUrl
  capacity
  createdAt
  updatedAt
  recurrenceId
  recurrenceConfig {
    recurrenceEndDate
    frequency
    repeatsOn
  }
  createdBy {
    member {
      ${MemberGQLFields('basic')}
      profilePicture {
        ${MediaGQLFields('all')}
      }
    }
  }
  authMemberProps {
    eventRegistration {
      ${EventRegistrationGQLFields('all')}
    }
  }
  customSeoDetail {
    ${CustomSeoDetailGQLFields('basic')}
  }
  space {
    ${SpaceGQLFields('basic')}
  }
  hosts {
    ${MemberGQLFields('basic')}
    profilePicture {
      ${MediaGQLFields('all')}
    }
  }
  mentions {
    ${MemberGQLFields('basic')}
  }
  registrationsCount
  recentRegistrations {
    id
    cancelledAt
    eventId
    registeredAt
    status
    member {
      id
      name
      relativeUrl
      profilePicture {
        ${MediaGQLFields('all')}
      } 
    }
  }
  physicalLocations {
    id
    displayName
    formattedAddress
    placeId
  }
  virtualLocations {
    id
    url
    provider
  }
  thumbnail {
    ${MediaGQLFields('all')}
  }
  tagIds
  tags {
    ${TagGQLFields('basic')}
  }
`

const EVENT_DETAIL_FIELDS = `
  id
  title
  content
  shortContent
  startTime
  endTime
  timeZone
  slug
  status
  url
  relativeUrl
  capacity
  createdAt
  updatedAt
  recurrenceId
  recurrenceConfig {
    recurrenceEndDate
    repeatsOn
    frequency
  }
  authMemberProps {
    eventRegistration {
      ${EventRegistrationGQLFields('all')}
    }
    permissions {
      ${ActionPermissionsGQLFields('all')}
    }
  }
  customSeoDetail {
    ${CustomSeoDetailGQLFields('basic')}
  }
  space {
    ${SpaceGQLFields('basic')}
  }
  attachments {
    ${FileGQLFields('basic')}
  }
  images {
    ${MediaGQLFields('all')}
  }
  embeds {
    ${EmbedGQLFields('basic')}
  }
  hosts {
    ${MemberGQLFields('basic')}
    profilePicture {
      ${MediaGQLFields('all')}
    }
  }
  mentions {
    ${MemberGQLFields('basic')}
  }
  notificationSettings {
    ${EventNotificationSettingsGQLFields('basic')}
  }
  registrationsCount
  recentRegistrations {
    id
    cancelledAt
    eventId
    registeredAt
    status
    member {
      id
      name
      relativeUrl
      profilePicture {
        ${MediaGQLFields('all')}
      } 
    }
  }
  physicalLocations {
    id
    additionalData
    displayName
    formattedAddress
    latitude
    longitude
    placeId
    plusCode
    mapUrls(input: { height: 200 }) {
      lightUrl
      darkUrl
    }
  }
  virtualLocations {
    id
    url
    provider
  }
  thumbnail {
    ${MediaGQLFields('all')}
  }
  tagIds
  tags {
    ${TagGQLFields('basic')}
  }
`

export function createEventGQLQuery() {
  return gql`
    mutation createEvent($input: CreateEventInput!, $spaceId: ID!) {
      createEvent(input: $input, spaceId: $spaceId) {
        ${EVENT_LIST_FIELDS}
      }
    }
  `
}

export function updateEventGQLQuery() {
  return gql`
    mutation updateEvent($eventId: ID!, $input: UpdateEventInput!) {
      updateEvent(eventId: $eventId, input: $input) {
        ${EVENT_DETAIL_FIELDS}
      }
    }
  `
}

export function deleteEventGQLQuery(fields: ActionFields) {
  return gql`
    mutation deleteEvent($eventId: ID!, $input: DeleteEventInput) {
      deleteEvent(eventId: $eventId, input: $input) {
        ${ActionGQLFields(fields)}
      }
    }
  `
}

export function eventsGQLQuery() {
  return gql`
    query Events(
      $after: String
      $before: String
      $createdById: ID
      $endTime: EventDateFilterInput
      $startTime: EventDateFilterInput 
      $orderBy: EventListOrderByEnum
      $eventIds: [ID!]
      $hostIds: [ID!]
      $limit: Int!
      $offset: Int
      $query: String
      $reverse: Boolean
      $spaceIds: [ID!]
      $tagIds: [ID!]
      $recurrenceMode: GetEventsRecurrenceMode
    ) {
      events(
        after: $after
        before: $before
        createdById: $createdById
        endTime: $endTime
        startTime: $startTime
        orderBy: $orderBy
        eventIds: $eventIds
        hostIds: $hostIds
        limit: $limit
        offset: $offset
        query: $query
        reverse: $reverse
        spaceIds: $spaceIds
        tagIds: $tagIds
        recurrenceMode: $recurrenceMode
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        nodes {
          ${EVENT_LIST_FIELDS}
        }
      }
    }
  `
}

export function getHasOngoingEventsGQLQuery() {
  return gql`
    query Events(
      $after: String
      $before: String
      $createdById: ID
      $endTime: EventDateFilterInput
      $startTime: EventDateFilterInput
      $eventIds: [ID!]
      $hostIds: [ID!]
      $limit: Int!
      $offset: Int
      $query: String
      $reverse: Boolean
      $spaceIds: [ID!]
      $tagIds: [ID!]
    ) {
      events(
        after: $after
        before: $before
        createdById: $createdById
        endTime: $endTime
        startTime: $startTime
        eventIds: $eventIds
        hostIds: $hostIds
        limit: $limit
        offset: $offset
        query: $query
        reverse: $reverse
        spaceIds: $spaceIds
        tagIds: $tagIds
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        nodes {
          id
        }
      }
    }
  `
}

export function searchEventForEmbedGQLQuery() {
  return gql`
    query Events(
      $after: String
      $before: String
      $createdById: ID
      $endTime: EventDateFilterInput
      $startTime: EventDateFilterInput
      $eventIds: [ID!]
      $hostIds: [ID!]
      $limit: Int!
      $offset: Int
      $query: String
      $reverse: Boolean
      $spaceIds: [ID!]
      $tagIds: [ID!]
    ) {
      events(
        after: $after
        before: $before
        createdById: $createdById
        endTime: $endTime
        startTime: $startTime
        eventIds: $eventIds
        hostIds: $hostIds
        limit: $limit
        offset: $offset
        query: $query
        reverse: $reverse
        spaceIds: $spaceIds
        tagIds: $tagIds
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        nodes {
          id
          title
        }
      }
    }
  `
}

export function eventGQLQuery() {
  return gql`
    query Event($id: ID!) {
      event(id: $id) {
        ${EVENT_DETAIL_FIELDS}
      }
    }
  `
}

export function registerForEventGQLMutation(fields: ActionFields) {
  return gql`
    mutation registerForEvent($eventId: ID!, $input: RegisterForEventInput!) {
      registerForEvent(eventId: $eventId, input: $input) {
        ${ActionGQLFields(fields)}
      }
    }
  `
}

export function cancelEventRegistrationGQLMutation(fields: ActionFields) {
  return gql`
    mutation cancelEventRegistration($eventId: ID!, $input: CancelRegistrationInput) {
      cancelEventRegistration(eventId: $eventId, input: $input) {
        ${ActionGQLFields(fields)}
      }
    }
  `
}

export function eventRegistrationsGQLQuery(fields: EventRegistrationFields) {
  return gql`
    query eventRegistrations(
      $after: String
      $before: String
      $eventId: ID!
      $limit: Int!
      $offset: Int
      $reverse: Boolean
    ) {
      eventRegistrations(
        after: $after
        before: $before
        eventId: $eventId
        limit: $limit
        offset: $offset
        reverse: $reverse
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        nodes {
          ${EventRegistrationGQLFields(fields)}
        }
      }
    }
  `
}

export function autocompletePhysicalLocationsGQLQuery(
  fields: AutocompletePhysicalLocationResultFields,
) {
  return gql`
    query autocompletePhysicalLocations($input: AutocompletePhysicalLocationInput!) {
      autocompletePhysicalLocations(input: $input) {
        ${AutocompletePhysicalLocationResultGQLFields(fields)}
      }
    }
  `
}

export function getPlaceDetailsGQLQuery(fields?: PlaceDetailsFields) {
  return gql`
    query getPlaceDetails($input: PlaceDetailsInput!) {
      getPlaceDetails(input: $input) {
        ${PlaceDetailsGQLFields(fields)}
      }
    }
  `
}

export function generateStaticMapUrlGQLQuery() {
  return gql`
    query generateStaticMapUrl($input: StaticMapInput!) {
      generateStaticMapUrl(input: $input) {
        darkUrl
        lightUrl
      }
    }
  `
}

export function eventRecurrencesGQLQuery(fields: EventRecurrenceFields) {
  return gql`
    query EventRecurrences(
      $recurrenceId: ID!
      $limit: Int!
      $offset: Int
      $orderBy: EventRecurrenceOrderByEnum
      $reverse: Boolean
      $startTime: EventDateFilterInput
      $before: String
      $after: String
    ) {
      eventRecurrences(
        recurrenceId: $recurrenceId
        limit: $limit
        offset: $offset
        orderBy: $orderBy
        reverse: $reverse
        before: $before
        after: $after
        startTime: $startTime
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        nodes {
          ${EventRecurrenceGQLFields(fields)}
        }
      }
    }
  `
}

export function getCalendarInviteDataGQLQuery() {
  return gql`
    query getCalendarInviteData($eventId: ID!) {
      calendarInviteData(eventId: $eventId) {
        ${CalendarInviteDataGQLFields('all')}
      }
    }
  `
}

export function getEventAnalyticsGQLQuery(fields?: ReportRecordFields) {
  return gql`
    query eventAnalytics(
      $eventRecurrenceIds: [String!]
      $limit: Float
      $metric: EventMetric
      $offset: Float
    ) {
      eventAnalytics(
        eventRecurrenceIds: $eventRecurrenceIds
        limit: $limit
        metric: $metric
        offset: $offset
      ) {
        records {
          ${reportRecordGQLFields(fields)}
        }
      }
    }
  `
}
